// OP-3605 — Tela do contrato (ver ESL1; agir ESL11): a custódia do
// equipamento que está fora. Cada identificador aparece uma vez: o código do
// contrato no cabeçalho, o par pedido→remessa na linha da remessa.
// Encerrar é ato documental — não devolve equipamento; o caminho de volta é
// coleta, recebimento e conferência, na tela da remessa.
import { FC, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueLocais, useEstoqueRemessasDoVinculo, useEstoqueVinculoDetalhe } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { definirVigenciaEstoqueVinculo, encerrarEstoqueVinculo } from '../../../../Service/estoque'
import { rotuloMetodoEnvio, RemessaRow, StatusRemessa } from '../../../../types/estoque'
import {
  BD, Button, Card, DetailLayout, Field, Input, PageSubtitle, PageTitle, Spinner, StatusBadge, Table,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { AcaoDecisao, Decisao } from '../Decisao'
import { tratarErro } from '../erros'
import { hojeISO } from '../Solicitar/form'
import { STATUS_REMESSA_UI } from '../Remessa/regras'
import { PendenciasBadges, VigenciaBadge } from '../Vinculos'
import {
  NATUREZA_LABEL, atrasoRetorno, statusDoVinculoUi, avisoVigencia, impedimentoEncerrar, impedimentoVigencia, limiteRetorno, nomeDaPraca, ordenarPorRetorno,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const Bloco: FC<{ titulo: string; children: any }> = ({ titulo, children }) => (
  <Card style={{ padding: 16, marginBottom: 16 }}><div style={{ fontWeight: 600, marginBottom: 8 }}>{titulo}</div>{children}</Card>
)

/** Página de um bloco de remessas (25 por página, como o servidor devolve). */
const Paginacao: FC<{ page: number; lastPage: boolean; onPage: (p: number) => void }> = ({ page, lastPage, onPage }) => {
  if (page <= 1 && lastPage) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, paddingTop: 12 }}>
      <Button $variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹ Anterior</Button>
      <span style={{ fontSize: 13, color: BD.grayText }}>Página {page}</span>
      <Button $variant="secondary" disabled={lastPage} onClick={() => onPage(page + 1)}>Próxima ›</Button>
    </div>
  )
}

export const EstoqueVinculo: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const idNum = Number(id) || null
  const hoje = useMemo(hojeISO, [])
  const { pode } = usePerfilEstoque()
  const age = pode('VINCULOS')
  const { data: v, isLoading, isError, isUnauthorized, mutate } = useEstoqueVinculoDetalhe(idNum)
  // Dois blocos, duas consultas: o servidor separa abertas de fechadas, cada
  // uma com página própria. Derivar isso de uma página de 25 ordenada por id
  // fazia "Na rua agora" dizer que não havia equipamento fora quando havia.
  const [pagAbertas, setPagAbertas] = useState(1)
  const [pagFechadas, setPagFechadas] = useState(1)
  const abertasQ = useEstoqueRemessasDoVinculo(idNum, '1', pagAbertas)
  const fechadasQ = useEstoqueRemessasDoVinculo(idNum, '0', pagFechadas)
  const { locais } = useEstoqueLocais()
  const [busy, setBusy] = useState(false)
  const [vigencia, setVigencia] = useState('')
  const [confirmandoEncerrar, setConfirmandoEncerrar] = useState(false)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />
  if (isLoading) return <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
  if (isError || !v) {
    return <><PageTitle>Contrato</PageTitle><Card><div style={{ padding: 24, color: BD.grayText }}>Contrato não encontrado ou fora do seu escopo. <Button $variant="secondary" onClick={() => navigate('/interno/estoque/vinculos')}>Voltar</Button></div></Card></>
  }

  const executar = async (acao: () => Promise<any>, ok: string) => {
    setBusy(true)
    try {
      await acao()
      toast.success(ok)
      await Promise.all([mutate(), abertasQ.mutate(), fechadasQ.mutate()])
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await Promise.all([mutate(), abertasQ.mutate(), fechadasQ.mutate()])
    } finally {
      setBusy(false)
    }
  }

  const ui = statusDoVinculoUi(v, hoje)
  const praca = nomeDaPraca(locais, v.pracaId)
  // Ordenadas pelo limite efetivo dentro da página que veio (o servidor já
  // fez o corte por status). `remessasAbertas` do vínculo é o número oficial.
  const abertas = ordenarPorRetorno(abertasQ.rows)
  const fechadas = fechadasQ.rows
  const totalAbertas = Number(v.remessasAbertas)
  const atraso = atrasoRetorno(v.proximoRetorno, v.pecasFora, hoje)
  const impEncerrar = impedimentoEncerrar(v.status, v.remessasAbertas)
  const impVigencia = impedimentoVigencia(vigencia)
  const aviso = avisoVigencia(vigencia, hoje)
  const sugerida = !!Number(v.vigenciaSugerida)

  const linhaRemessa = (r: RemessaRow) => {
    const rui = STATUS_REMESSA_UI[r.status as StatusRemessa] ?? { bg: '#F3F4F6', color: '#374151', label: r.status }
    const ir = (e: any, para: string) => { e.preventDefault(); e.stopPropagation(); navigate(para) }
    return (
      <tr key={r.id}>
        <td style={{ fontFamily: 'monospace', fontSize: 13 }}>
          {r.solicitacaoId && <><a href={`/interno/estoque/fila/${r.solicitacaoId}`} onClick={(e) => ir(e, `/interno/estoque/fila/${r.solicitacaoId}`)}>pedido #{r.solicitacaoId}</a> → </>}
          <a href={`/interno/estoque/remessas/${r.id}`} onClick={(e) => ir(e, `/interno/estoque/remessas/${r.id}`)}><b>{r.codigo}</b></a>
        </td>
        <td><StatusBadge $bg={rui.bg} $color={rui.color}>{rui.label}</StatusBadge></td>
        <td style={{ fontSize: 13 }}>de {r.localOrigemNome} · {rotuloMetodoEnvio(r.metodoEnvio)}{r.transportadora ? ` (${r.transportadora})` : ''}</td>
        <td style={{ fontSize: 13 }}>{fmtDateTime(r.despachadoEm)}</td>
        <td style={{ fontSize: 13 }}>{fmtDate(r.entregueEm)}</td>
        <td style={{ fontSize: 13 }}>{fmtDate(limiteRetorno(r))}{r.prorrogadoPara && <span style={{ fontSize: 11, color: BD.grayText }}> (prorrogada)</span>}</td>
        <td style={{ fontSize: 13 }}>{fmtDateTime(r.retornoEm)}</td>
      </tr>
    )
  }
  const cabecalhoRemessas = <thead><tr><th>Pedido → remessa</th><th>Status</th><th>Envio</th><th>Despachada</th><th>Entregue</th><th>Retorno previsto</th><th>Retornou</th></tr></thead>

  const definirVigencia = () => executar(
    () => definirVigenciaEstoqueVinculo({ id: v.id, vigenciaFim: vigencia }),
    'Vigência definida',
  ).then(() => setVigencia(''))

  const encerrar = () => executar(() => encerrarEstoqueVinculo({ id: v.id }), 'Vínculo encerrado')
    .then(() => setConfirmandoEncerrar(false))

  const principal: AcaoDecisao | null = age && v.status === 'VIGENTE'
    ? {
      rotulo: 'Definir vigência',
      rodar: definirVigencia,
      impedimento: vigencia ? impVigencia : 'Escolha a data de vigência no formulário ao lado.',
    }
    : null

  // Encerrar é ato documental e não se desfaz: dois passos, com volta.
  const secundarias: AcaoDecisao[] = age && v.status === 'VIGENTE'
    ? (confirmandoEncerrar
      ? [
        // "Voltar" primeiro, no slot do botão que foi tocado: repetir o toque
        // desfaz em vez de encerrar.
        { rotulo: 'Voltar', rodar: () => setConfirmandoEncerrar(false), variante: 'secondary' as const },
        { rotulo: `Confirmar o encerramento de ${v.codigo}`, rodar: encerrar, variante: 'secondary' as const },
      ]
      : [{ rotulo: 'Encerrar vínculo', rodar: () => setConfirmandoEncerrar(true), impedimento: impEncerrar, variante: 'secondary' as const }])
    : []

  return (
    <DetailLayout $contido>
      <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>{v.codigo} <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></PageTitle>
          <PageSubtitle>
            <b>{v.detentorNome}</b> · {NATUREZA_LABEL[v.natureza] ?? v.natureza} · praça {praca}
            {' · '}vigência {fmtDate(v.vigenciaInicio)} → {v.vigenciaFim ? fmtDate(v.vigenciaFim) : 'sem data'}{' '}
            <VigenciaBadge v={v} hoje={hoje} />
            {v.vigenciaFim && <span style={{ fontSize: 12, color: BD.grayText }}> {sugerida ? '(sugerida pelo retorno previsto do evento)' : '(definida pela logística)'}</span>}
            {v.status !== 'VIGENTE' && v.encerradoEm && <> · encerrado {fmtDateTime(v.encerradoEm)}</>}
          </PageSubtitle>
        </div>
        <Button $variant="secondary" onClick={() => navigate('/interno/estoque/vinculos')}>‹ Contratos</Button>
      </div>

      <Card style={{ padding: 16, marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <div><div style={{ fontSize: 12, color: BD.grayText }}>Pedidos</div><div style={{ fontSize: 20, fontWeight: 600 }}>{Number(v.requisicoes)}</div><div style={{ fontSize: 12, color: BD.grayText }}>deram origem a remessas deste contrato</div></div>
        <div>
          <div style={{ fontSize: 12, color: BD.grayText }}>Na rua agora</div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{Number(v.pecasFora)}</div>
          <div style={{ fontSize: 12, color: BD.grayText }}>{totalAbertas ? `equipamentos, em ${totalAbertas} remessa${totalAbertas === 1 ? '' : 's'}` : 'nada fora por este contrato'}</div>
          <PendenciasBadges v={v} alinhar="flex-start" />
        </div>
        <div>
          <div style={{ fontSize: 12, color: BD.grayText }}>Retorno</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: atraso > 0 ? BD.red : undefined }}>{atraso > 0 ? `${atraso}d de atraso` : fmtDate(v.proximoRetorno)}</div>
          <div style={{ fontSize: 12, color: BD.grayText }}>{atraso > 0 ? 'além do previsto mais próximo' : v.proximoRetorno ? 'previsão mais próxima' : Number(v.pecasFora) === 0 ? 'tudo devolvido e conferido' : 'nada a devolver'}</div>
        </div>
        {Number(v.consumoEnviado) > 0 && (
          <div>
            <div style={{ fontSize: 12, color: BD.grayText }}>Consumo enviado</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{Number(v.consumoEnviado)}</div>
            <div style={{ fontSize: 12, color: BD.grayText }} title="Material entregue e baixado do patrimônio. Não é medição do que o produtor imprimiu — consumo não tem retorno a conferir.">
              {v.consumo.map((c) => `${Number(c.quantidade)} ${c.nome}`).join(' · ')}
            </div>
          </div>
        )}
      </Card>

      {!age && v.status === 'VIGENTE' && <Card style={{ padding: 12, marginBottom: 16, color: BD.grayText }}>Definir a vigência ou encerrar exige a permissão "Vínculos (contratos e termos)".</Card>}

      {age && v.status === 'VIGENTE' && (
        <Bloco titulo="Definir a data de retorno do contrato">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
            A data sugerida vem do retorno previsto do último evento. Ao fixar, ela deixa de ser sugestão — e um pedido novo que vá além dela volta a esticá-la.
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field><label>Vigência até</label><Input type="date" value={vigencia} onChange={(e) => setVigencia(e.target.value)} /></Field>
            {(impVigencia && vigencia) && <span style={{ fontSize: 13, color: BD.grayText }}>{impVigencia}</span>}
            {aviso && <span style={{ fontSize: 13, color: '#92400E' }}>{aviso}</span>}
          </div>
        </Bloco>
      )}

      <Bloco titulo={`Na rua agora · ${totalAbertas} remessa${totalAbertas === 1 ? '' : 's'} aberta${totalAbertas === 1 ? '' : 's'}`}>
        {abertasQ.isLoading ? <div style={{ textAlign: 'center', padding: 16 }}><Spinner /></div>
          : abertasQ.isError ? (
            <div style={{ color: BD.red, fontSize: 13 }}>
              Não foi possível carregar as remessas abertas. <Button $variant="secondary" onClick={() => abertasQ.mutate()}>Tentar de novo</Button>
            </div>
          ) : abertas.length === 0 ? (
            <div style={{ color: BD.grayText, fontSize: 13 }}>Nenhum equipamento fora por este contrato agora.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}><Table>{cabecalhoRemessas}<tbody>{abertas.map(linhaRemessa)}</tbody></Table></div>
              <Paginacao page={pagAbertas} lastPage={!!abertasQ.lastPage} onPage={setPagAbertas} />
              <div style={{ fontSize: 12, color: BD.grayText, marginTop: 8 }}>Entrega, coleta, recebimento e conferência acontecem na tela da remessa. Enquanto houver remessa aqui, o contrato não encerra.</div>
            </>
          )}
      </Bloco>

      <Bloco titulo={`Já voltou · ${fechadasQ.total} remessa${fechadasQ.total === 1 ? '' : 's'} encerrada${fechadasQ.total === 1 ? '' : 's'} ou cancelada${fechadasQ.total === 1 ? '' : 's'}`}>
        {fechadasQ.isLoading ? <div style={{ textAlign: 'center', padding: 16 }}><Spinner /></div>
          : fechadasQ.isError ? (
            <div style={{ color: BD.red, fontSize: 13 }}>
              Não foi possível carregar as remessas fechadas. <Button $variant="secondary" onClick={() => fechadasQ.mutate()}>Tentar de novo</Button>
            </div>
          ) : fechadas.length === 0 ? (
            <div style={{ color: BD.grayText, fontSize: 13 }}>Nenhuma remessa deste contrato foi encerrada ainda.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}><Table>{cabecalhoRemessas}<tbody>{fechadas.map(linhaRemessa)}</tbody></Table></div>
              <Paginacao page={pagFechadas} lastPage={!!fechadasQ.lastPage} onPage={setPagFechadas} />
            </>
          )}
      </Bloco>

      {v.observacao && <Bloco titulo="Observação">{v.observacao}</Bloco>}
      </div>

      <Decisao
        estado={ui}
        desde={v.vigenciaFim ? `vigente até ${fmtDate(v.vigenciaFim)}` : 'vigência a definir'}
        principal={principal}
        secundarias={secundarias}
        pendencia="Encerrar é ato documental: não devolve equipamento, e não encerra enquanto houver remessa aberta."
        busy={busy}
        aoFechar={() => setConfirmandoEncerrar(false)}
      />
    </DetailLayout>
  )
}

export default EstoqueVinculo
